import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton, SecondaryButton, DangerButton } from "@/components/Buttons.jsx";
import { addToast, checkSession } from "@/store/app/action.js";
import { getCommissionForSeller, deactivateCommissionForSeller } from "@/api/commissions.js";
import ModalBasic from "@/components/ModalBasic.jsx";

function CommissionsStorePanel() {
  const { user, selectedLocation } = useSelector((state) => state.app);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [commissionPeriods, setCommissionPeriods] = useState([]);
  const [loadingCommissions, setLoadingCommissions] = useState(false);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [selectedPeriodToDeactivate, setSelectedPeriodToDeactivate] = useState(null);
  const [deactivating, setDeactivating] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
  email: "",
  });

  // Initialize form data when user data changes
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
      });
    }
  }, [user]);

  // Fetch commission periods for non-admin users
  useEffect(() => {
    const fetchCommissions = async () => {
      let sellerId = selectedLocation?.id;
      if (sellerId) {

        setLoadingCommissions(true);
        try {
          const data = await getCommissionForSeller(sellerId);
          setCommissionPeriods(data || []);
        } catch (error) {
          console.error("Error fetching commission periods:", error);
          dispatch(
            addToast({
              type: "error",
              message: "Failed to load commission periods",
              timer: 5,
            })
          );
        } finally {
          setLoadingCommissions(false);
        }
      }
    };

    fetchCommissions();
  }, [user, selectedLocation, dispatch]);
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  /*const checkDirty = (e) => {
    // Check if any field has been modified from its initial value
    const keysToCheck = ["first_name", "last_name", "email"];
    const dirty = keysToCheck.some((key) => {
      const currentValue =
        e.target.name === key ? e.target.value : formData[key];
      return user[key] !== currentValue;
    });
    setAmSoDirty(dirty);
  };
  const submit = async (e) => {
    e.preventDefault();
    const data = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
    };
    try {
      await updateMerchantUser(data);
      dispatch(
        addToast({ type: "success", message: "Account updated", timer: 5 })
      );
      try {
        await dispatch(checkSession());
      } catch (e) {
        // If VPN check fails during account update, show error
        dispatch(
          addToast({
            type: "error",
            message:
              e.message || "Session validation failed. Please log in again.",
            timer: 10,
          })
        );
      }
      setAmSoDirty(false);
    } catch (e) {
      let message = "please try again";
      try {
        const serverMessage = e.response?.data?.error;
        if (serverMessage) {
          message = serverMessage;
        }
      } catch {}

      dispatch(
        addToast({
          type: "error",
          message: "Error updating account " + message,
          timer: 5,
        })
      );
    } finally {
      setLoading(false);
    }
  };*/

  const handleDeactivateClick = (period) => {
    setSelectedPeriodToDeactivate(period);
    setDeactivateModalOpen(true);
  };

  const handleDeactivate = async (endType) => {
    if (!selectedPeriodToDeactivate) return;
    
    try {
      setDeactivating(true);
      await deactivateCommissionForSeller(
        selectedPeriodToDeactivate._id,
        selectedLocation?.id,
        endType === 'period-end'
      );
      
      dispatch(
        addToast({
          type: "success",
          message: `Commission period ${endType === 'immediate' ? 'ended immediately' : 'will end at period end'}`,
          timer: 5,
        })
      );
      
      // Refresh commission periods
      const data = await getCommissionForSeller(selectedLocation?.id);
      setCommissionPeriods(data || []);
      
      setDeactivateModalOpen(false);
      setSelectedPeriodToDeactivate(null);
    } catch (error) {
      console.error("Error deactivating commission period:", error);
      dispatch(
        addToast({
          type: "error",
          message: error.response?.data?.message || "Failed to deactivate commission period",
          timer: 5,
        })
      );
    } finally {
      setDeactivating(false);
    }
  };

  const renderCommissionCard = (period) => {
    const template = period.template_id || {};
    const isActive = period.active && new Date(period.period_end) > new Date();
    let grossSales = period.gross_sales_this_period;
    if(period.template_id.product_category == "tobacco"){
      grossSales = period.gross_sales_tobacco_this_period;
    } else if(period.template_id.product_category == "non-tobacco"){
      grossSales = period.gross_sales_non_tobacco_this_period;
    }
    return (
      <div
        key={period._id}
        className={`p-6 rounded-lg border-2 ${
          isActive
            ? "border-indigo-500 bg-white dark:bg-slate-800"
            : "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50"
        } shadow-sm`}
      >
        {/* Header */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {template.name || "Commission Period"}
          </h3>
          <div className="flex items-center justify-between mt-2">
            <div>
              {isActive && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                  Active
                </span>
              )}
            </div>
            {user.type === "saucey_admin" && isActive && (
              <button
                onClick={() => handleDeactivateClick(period)}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
              >
                Deactivate
              </button>
            )}
          </div>
        </div>

        {/* Period Dates */}
        <div className="mb-4 text-sm text-slate-600 dark:text-slate-400">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium">Period:</span>
            <span>
              {formatDate(period.period_start)} - {formatDate(period.period_end)}
            </span>
          </div>
        </div>

        {/* Sales Information */}
        <div className="mb-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-slate-600 dark:text-slate-400">Gross Sales:</span>
            <span className="font-semibold text-slate-800 dark:text-slate-100">
              ${(grossSales || 0).toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600 dark:text-slate-400">
              Commissions Collected:
            </span>
            <span className="font-semibold text-indigo-600 dark:text-indigo-400">
              ${(period.commissions_collected_this_period || 0).toFixed(2)}
            </span>
          </div>
        </div>

        {/* Commission Type Specific Display */}
        {template.commission_type === "tiered" && template.tiers && (
          <div className="mt-4">
            <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">
              Progress to Next Tier
            </div>
            {renderTieredProgress(period, template)}
          </div>
        )}

        {template.commission_type === "percentage" && (
          <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded">
            <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">
              Commission Rate
            </div>
            <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
              {(template.commission_rate * 100).toFixed(2)}%
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTieredProgress = (period, template) => {
    const grossSales = period.gross_sales_non_tobacco_this_period || 0;
    // Create a deep copy of tiers to avoid mutating the original
    const tiers = (template.tiers || []).map(tier => ({ ...tier }));
    // Find current tier
    for(let i = 1; i < tiers.length; i++){
      tiers[i].rate += tiers[i-1].rate;
    }

    let tierIdx = tiers.findIndex((tier)=> {
      return grossSales >= tier.min && grossSales < tier.max;
    });
    let displayTiers = [], startIndex = 0, currentTierIndex = 0;
    if(tierIdx > 0 && tierIdx < tiers.length - 1){
      displayTiers = tiers.slice(tierIdx - 1, tierIdx + 2);
      startIndex = tierIdx - 1;
      currentTierIndex = tierIdx;
    } else if(tierIdx === 0){
      displayTiers = tiers.slice(0, 3);
      startIndex = 0;
      currentTierIndex = 0;
    } else if(tierIdx === tiers.length - 1){
      displayTiers = tiers.slice(-3);
      startIndex = tiers.length - 3;
      currentTierIndex = tiers.length - 1;
    }

    return (
      <div className="space-y-3">
        {displayTiers.map((tier, index) => {
          const actualIndex = startIndex + index;
          const isCurrent = actualIndex === currentTierIndex;
          const isComplete = grossSales >= tier.min;
          
          // Calculate progress within this tier
          let progress = 0;
          if (isComplete && actualIndex < tiers.length - 1) {
            const nextTier = tiers[actualIndex + 1];
            const tierRange = nextTier.min - tier.min;
            const progressInTier = grossSales - tier.min;
            progress = Math.min((progressInTier / tierRange) * 100, 100);
          } else if (isComplete) {
            progress = 100;
          } else if (actualIndex === 0) {
            progress = (grossSales / tier.min) * 100;
          }

          return (
            <div key={tier._id || index}>
              <div className="flex justify-between text-xs mb-1">
                <span className={`font-medium ${isCurrent ? "text-indigo-600 dark:text-indigo-400" : "text-slate-600 dark:text-slate-400"}`}>
                  Tier {actualIndex + 1}: ${tier.rate.toFixed(2)}
                </span>
                <span className="text-slate-500 dark:text-slate-500">
                  ${tier.min.toLocaleString()}
                  {tiers[actualIndex + 1] && ` - ${tiers[actualIndex + 1].min.toLocaleString()}`}
                </span>
              </div>
              <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    isCurrent
                      ? "bg-green-500 dark:bg-indigo-500"
                      : isComplete
                      ? "bg-indigo-600 dark:bg-green-600"
                      : "bg-slate-300 dark:bg-slate-600"
                  }`}
                  style={{ width: `${isCurrent ? progress : isComplete ? 100 : 0}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="grow">
      {/* Panel body */}
      <div className="p-6 space-y-6">
        <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-5">
          My Store
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">
          {selectedLocation?.name}
        </p>
      </div>

      {/* Commission Periods Section */}
      {commissionPeriods.length > 0 && (
        <div className="p-6 border-t border-slate-200 dark:border-slate-700">
          <section>
            <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
              Commission Periods
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">
              View your active and past commission periods
            </p>

            {loadingCommissions ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : commissionPeriods.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {commissionPeriods.map((period) => renderCommissionCard(period))}
              </div>
            ) : (
              <div className="text-center py-12 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                <p className="text-slate-600 dark:text-slate-400">
                  No commission periods found
                </p>
              </div>
            )}
          </section>
        </div>
      )}

      {/* Deactivate Confirmation Modal */}
      <ModalBasic
        modalOpen={deactivateModalOpen}
        setModalOpen={setDeactivateModalOpen}
        title="Deactivate Commission Period"
        width="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            How would you like to end this commission period?
          </p>

          {selectedPeriodToDeactivate && (
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
              <div className="text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                {selectedPeriodToDeactivate.template_id?.name || "Commission Period"}
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">
                {formatDate(selectedPeriodToDeactivate.period_start)} - {formatDate(selectedPeriodToDeactivate.period_end)}
              </div>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={() => handleDeactivate('period-end')}
              disabled={deactivating}
              className="w-full px-4 py-3 text-left border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="font-medium text-slate-800 dark:text-slate-100 mb-1">
                End at Period End
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">
                Commission period will continue until {selectedPeriodToDeactivate && formatDate(selectedPeriodToDeactivate.period_end)}
              </div>
            </button>

            <button
              onClick={() => handleDeactivate('immediate')}
              disabled={deactivating}
              className="w-full px-4 py-3 text-left border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="font-medium text-slate-800 dark:text-slate-100 mb-1">
                End Immediately
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400">
                Commission period will end right now
              </div>
            </button>
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t border-slate-200 dark:border-slate-700">
            <SecondaryButton
              onClick={() => {
                setDeactivateModalOpen(false);
                setSelectedPeriodToDeactivate(null);
              }}
              disabled={deactivating}
            >
              Cancel
            </SecondaryButton>
          </div>
        </div>
      </ModalBasic>
    </div>
  );
}

export default CommissionsStorePanel;
